import { useState, useEffect, useCallback, useRef } from "react";
import { ChevronLeft, ChevronRight, Star, Quote } from "lucide-react";
import Reveal from "@/components/Reveal";

const reviews = [
  {
    name: "Laura Pion",
    date: "il y a 2 mois",
    rating: 5,
    text: "J'ai fait appel récemment à cette conciergerie et je suis vraiment ravi. Service serieux, réactivité au top et un vrai sens du detail. Tres professionnelle et à l'écoute, je recommande vivement !",
  },
  {
    name: "Chihab Amari",
    date: "il y a 4 mois",
    rating: 5,
    text: "L'équipe est professionnelle, réactive et tres organisée, que ce soit pour la remise des clés, le menage entre deux locations ou la gestion complete des appartements Airbnb à Aigues-Mortes et aux alentours. On sent un vrai souci du detail et de la qualité du service.",
  },
  {
    name: "Delsart Aude",
    date: "il y a 4 mois",
    rating: 5,
    text: "Valérie est attentive, à l'écoute et particulierement disponible. Je recommande",
  },
  {
    name: "Oceanne Loidon",
    date: "il y a 4 mois",
    rating: 5,
    text: "J'ai confié la gestion de mon appartement de Carnon à Golden Sea Conciergerie, et je ne peux que recommander leurs services. Valérie est professionnelle, réactive et toujours à l'écoute.",
  },
  {
    name: "Lea Cabrita",
    date: "il y a 6 mois",
    rating: 5,
    text: "J'ai loué avec mon conjoint un logement situé à la Grande Motte géré par Valérie. Le logement était plus que propre, et décoré avec tres bon gout. Nous étions ravis !",
  },
  {
    name: "Olivier Martello",
    date: "il y a 6 mois",
    rating: 5,
    text: "Nous avons fait appel à Valérie et nous sommes ravis de ses services. Elle a su nous conseiller et nous accompagner, dans la location de notre logement. Faire appel à ses services nous a permis d'augmenter considérablement notre taux d'occupation !",
  },
  {
    name: "Sophie Chabanon-Pouget",
    date: "il y a 8 mois",
    rating: 5,
    text: "Valérie a géré mon appartement d'une manière irréprochable ! Merci encore pour votre travail soigné et votre efficacité. Je n'hésiterais pas à refaire appel à vous !",
  },
  {
    name: "Fabienne Vangysel",
    date: "il y a 8 mois",
    rating: 5,
    text: "Valérie est une personne tres professionnelle : disponible, à l'écoute et bienveillante. Je recommande vivement Golden Sea Conciergerie.",
  },
];

const GoogleIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" className="flex-shrink-0">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
  </svg>
);

const VerifiedBadge = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" className="inline-block ml-1">
    <circle cx="12" cy="12" r="10" fill="#1a73e8"/>
    <path d="M9.5 12.5l2 2 4-4" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

export default function ReviewsCarousel() {
  const [page, setPage] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const touchStartX = useRef(0);
  const cardsPerPage = typeof window !== 'undefined' && window.innerWidth < 768 ? 1 : 3;
  const totalPages = Math.ceil(reviews.length / cardsPerPage);

  const goToPage = useCallback((newPage) => {
    setIsTransitioning(true);
    setTimeout(() => {
      setPage(newPage);
      setIsTransitioning(false);
    }, 300);
  }, []);

  const nextPage = useCallback(() => {
    goToPage((page + 1) % totalPages);
  }, [page, totalPages, goToPage]);

  const prevPage = useCallback(() => {
    goToPage((page - 1 + totalPages) % totalPages);
  }, [page, totalPages, goToPage]);

  useEffect(() => {
    const interval = setInterval(nextPage, 7000);
    return () => clearInterval(interval);
  }, [nextPage]);

  const visibleReviews = [];
  for (let i = 0; i < cardsPerPage; i++) {
    visibleReviews.push(reviews[(page * cardsPerPage + i) % reviews.length]);
  }

  const handleTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const handleTouchEnd = (e) => {
    const diff = touchStartX.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 50) { diff > 0 ? nextPage() : prevPage(); }
  };

  return (
    <section className="relative py-20 sm:py-28 bg-white overflow-hidden" data-testid="google-reviews-carousel">
      {/* Ligne décorative top */}
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#e8e4d9] to-transparent" />

      <div className="section-container relative z-10">
        {/* Header */}
        <Reveal>
          <div className="text-center mb-14 sm:mb-16">
            <p
              className="text-[10px] sm:text-[11px] font-semibold tracking-[0.2em] uppercase text-[#d4af37] mb-5"
              style={{ fontFamily: 'var(--font-sans)' }}
            >
              Témoignages
            </p>
            <h2
              className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#111] mb-6"
              style={{ fontFamily: 'var(--font-display)', lineHeight: 1.15 }}
            >
              Ce que disent nos <span className="text-[#d4af37]">clients</span>
            </h2>
            <div className="w-12 h-0.5 bg-[#d4af37] mx-auto rounded-full" />
          </div>
        </Reveal>

        {/* Google Rating Badge */}
        <Reveal delay={0.1}>
          <div className="flex items-center justify-center gap-6 mb-14 sm:mb-16">
            <div className="flex items-center gap-4 bg-[#fbfaf7] border border-[#f1eee6] rounded-2xl px-6 sm:px-8 py-4 sm:py-5 shadow-[0_4px_20px_rgba(0,0,0,0.04)]">
              {/* Google logo mini */}
              <div className="flex items-center gap-2">
                <GoogleIcon />
                <span className="text-[#333] text-sm sm:text-base font-semibold" style={{ fontFamily: 'var(--font-sans)' }}>
                  Google
                </span>
              </div>
              {/* Séparateur */}
              <div className="w-px h-8 bg-[#e8e4d9]" />
              {/* Note */}
              <div className="flex flex-col items-center">
                <div className="flex items-center gap-0.5 mb-0.5">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 sm:w-5 sm:h-5 fill-[#fbbc04] text-[#fbbc04]" />
                  ))}
                </div>
                <p className="text-[#999] text-[11px] sm:text-xs" style={{ fontFamily: 'var(--font-sans)' }}>
                  5/5 — <span className="text-[#555] font-medium">14 avis</span>
                </p>
              </div>
            </div>
          </div>
        </Reveal>

        {/* Review Cards */}
        <div
          className="relative"
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          <div className={`grid grid-cols-1 md:grid-cols-3 gap-5 sm:gap-6 transition-all duration-300 ${isTransitioning ? 'opacity-0 translate-y-3' : 'opacity-100 translate-y-0'}`}>
            {visibleReviews.map((review, i) => (
              <Reveal key={`${page}-${i}`} delay={i * 0.08}>
                <div
                  data-testid="google-review-card"
                  className="group relative bg-white rounded-2xl border border-[#f1eee6] p-6 sm:p-7 h-full flex flex-col shadow-[0_4px_20px_rgba(0,0,0,0.04)] hover:shadow-[0_8px_30px_rgba(0,0,0,0.08)] hover:border-[#e8e4d9] transition-all duration-500"
                >
                  {/* Quote icon décoratif */}
                  <div className="absolute top-5 right-5 opacity-[0.06] group-hover:opacity-[0.12] transition-opacity duration-500">
                    <Quote className="w-12 h-12 text-[#d4af37]" />
                  </div>

                  {/* Header: Avatar + Name + Google */}
                  <div className="flex items-center justify-between mb-5">
                    <div className="flex items-center gap-3">
                      {/* Avatar avec initiale */}
                      <div className="w-11 h-11 rounded-full bg-gradient-to-br from-[#d4af37] to-[#c19b2e] flex items-center justify-center text-white text-sm font-bold flex-shrink-0 shadow-[0_4px_12px_rgba(212,175,55,0.25)]"
                        style={{ fontFamily: 'var(--font-sans)' }}
                      >
                        {review.name.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-[#111] leading-tight" style={{ fontFamily: 'var(--font-sans)' }}>
                          {review.name}
                        </p>
                        <p className="text-[11px] text-[#aaa] mt-0.5" style={{ fontFamily: 'var(--font-sans)' }}>
                          {review.date}
                        </p>
                      </div>
                    </div>
                    <GoogleIcon />
                  </div>

                  {/* Stars + Verified */}
                  <div className="flex items-center gap-0.5 mb-4">
                    {[...Array(review.rating)].map((_, j) => (
                      <Star key={j} className="w-4 h-4 fill-[#fbbc04] text-[#fbbc04]" />
                    ))}
                    <VerifiedBadge />
                  </div>

                  {/* Review text */}
                  <p className="text-[#555] text-sm leading-relaxed flex-1" style={{ fontFamily: 'var(--font-sans)' }}>
                    {review.text.length > 200
                      ? review.text.substring(0, 200) + '...'
                      : review.text
                    }
                  </p>
                  {review.text.length > 200 && (
                    <p className="text-[11px] text-[#d4af37] mt-3 cursor-pointer hover:text-[#b89630] transition-colors font-medium" style={{ fontFamily: 'var(--font-sans)' }}>
                      Lire la suite
                    </p>
                  )}

                  {/* Bottom accent line */}
                  <div className="absolute bottom-0 left-6 right-6 h-px bg-gradient-to-r from-transparent via-[#d4af37]/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                </div>
              </Reveal>
            ))}
          </div>

          {/* Navigation Arrows */}
          <button
            onClick={prevPage}
            aria-label="Avis précédents"
            className="hidden md:flex absolute -left-5 lg:-left-8 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white border border-[#e8e4d9] shadow-md items-center justify-center text-[#999] hover:border-[#d4af37] hover:text-[#d4af37] transition-all duration-300 z-10"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            onClick={nextPage}
            data-testid="google-reviews-next-button"
            aria-label="Avis suivants"
            className="hidden md:flex absolute -right-5 lg:-right-8 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white border border-[#e8e4d9] shadow-md items-center justify-center text-[#999] hover:border-[#d4af37] hover:text-[#d4af37] transition-all duration-300 z-10"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>

        {/* Page dots */}
        <div className="flex justify-center gap-2.5 mt-10">
          {[...Array(totalPages)].map((_, i) => (
            <button
              key={i}
              onClick={() => goToPage(i)}
              className={`h-1.5 rounded-full transition-all duration-500 ${page === i ? 'bg-[#d4af37] w-8' : 'bg-[#e5e2da] w-2 hover:bg-[#d4af37]/30'}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
